import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, Clock, AlertTriangle, ChevronRight, TrendingUp } from 'lucide-react';

const AssessmentProgressWidget = ({ assessments = [] }) => {
  const navigate = useNavigate();
  
  // Get assessment stats
  const inProgress = assessments.filter(a => a.status !== 'Completed');
  const completed = assessments.filter(a => a.status === 'Completed');
  const avgScore = assessments.length > 0 
    ? Math.round(assessments.reduce((acc, a) => acc + (a.score || 0), 0) / assessments.length)
    : 0;

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-400';
    if (score >= 50) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getProgressBarColor = (score) => {
    if (score >= 75) return 'bg-green-500';
    if (score >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const typeConfig = {
    'CMMC': { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
    'HIPAA': { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
    'NIST_CSF': { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' }
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-violet-500/20 p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-violet-400" />
          Assessment Progress Overview
        </h3>
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1 text-yellow-400">
            <Clock className="w-4 h-4" />
            {inProgress.length} In Progress
          </span>
          <span className="flex items-center gap-1 text-green-400">
            <CheckCircle className="w-4 h-4" />
            {completed.length} Completed
          </span>
        </div>
      </div>

      {/* Mini progress cards */}
      {assessments.length === 0 ? (
        <p className="text-slate-400 text-sm">No assessments yet. Create one to get started!</p>
      ) : (
        <div className="space-y-3">
          {assessments.slice(0, 5).map((assessment) => {
            const config = typeConfig[assessment.assessmentType] || typeConfig['HIPAA'];
            const progress = assessment.totalQuestions > 0 
              ? Math.round((assessment.answeredQuestions / assessment.totalQuestions) * 100)
              : 0;
            const displayType = assessment.assessmentType === 'NIST_CSF' ? 'NIST' : assessment.assessmentType;

            return (
              <div
                key={assessment.id}
                onClick={() => navigate(`/assessment/${assessment.id}`)}
                className="flex items-center gap-4 p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 cursor-pointer transition-colors group"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.bg}`}>
                  <Shield className={`w-5 h-5 ${config.text}`} />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium truncate">{assessment.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${config.bg} ${config.text}`}>
                      {displayType}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <div className="flex-1 h-1.5 bg-slate-600 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${getProgressBarColor(assessment.score)}`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-xs text-slate-400">
                      {assessment.answeredQuestions}/{assessment.totalQuestions}
                    </span>
                  </div>
                </div>

                <div className="text-right">
                  <span className={`text-lg font-bold ${getScoreColor(assessment.score)}`}>
                    {assessment.score}%
                  </span>
                </div>

                <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-violet-400 transition-colors" />
              </div>
            );
          })}
          
          {assessments.length > 5 && (
            <p className="text-center text-slate-400 text-sm pt-2">
              +{assessments.length - 5} more assessments
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AssessmentProgressWidget;
